export interface MatchableCourse {
  id: number;
  name: string;
  price: number;
  level: string;
  description: string;
}

const BUDGET_RANGES: { test: RegExp; min: number; max: number }[] = [
  { test: /до\s*\$?\s*200|up to\s*\$?\s*200/i, min: 0, max: 200 },
  { test: /\$?\s*300\s*[–-]\s*\$?\s*400/, min: 300, max: 400 },
  { test: /\$?\s*500\s*[–-]\s*\$?\s*700/, min: 500, max: 700 },
  { test: /\$?\s*800\s*[–-]\s*\$?\s*1000/, min: 800, max: 1000 },
  { test: /\$?\s*1500\s*[–-]\s*\$?\s*2000/, min: 1500, max: 2000 },
  { test: /\$?\s*2500\+|\$?\s*2500\s*\+/i, min: 2500, max: 3000 },
];

const INTEREST_HINTS: { test: RegExp; names: string[] }[] = [
  { test: /спот|spot/i, names: ['Spot Strategist', 'Smart Trader', 'Crypto Starter', 'Crypto Base'] },
  { test: /фьючерс|futures|плеч|leverage/i, names: ['Futures Master', 'Smart Trader', 'Whale Analyst'] },
  { test: /defi|инвест/i, names: ['DeFi Investor', 'Altcoin Hunter', 'Crypto Empire'] },
  {
    test: /сопровожден|mentorship|support|персонал/i,
    names: ['VIP Mentorship', 'Crypto Empire', 'NFT & Metaverse Pro'],
  },
];

function parseBudgetRange(answer: string): { min: number; max: number } {
  for (const range of BUDGET_RANGES) {
    if (range.test.test(answer)) {
      return { min: range.min, max: range.max };
    }
  }

  const numbers = answer.match(/\d[\d\s,]*/g)?.map((n) => Number(n.replace(/[\s,]/g, ''))) ?? [];
  if (numbers.length >= 2) {
    return { min: Math.min(numbers[0], numbers[1]), max: Math.max(numbers[0], numbers[1]) };
  }
  if (numbers.length === 1) {
    return { min: 0, max: numbers[0] };
  }

  return { min: 0, max: 500 };
}

function interestScore(course: MatchableCourse, interest?: string): number {
  if (!interest) return 0;
  for (const hint of INTEREST_HINTS) {
    if (hint.test.test(interest)) {
      const index = hint.names.findIndex((name) => name === course.name);
      if (index >= 0) return 10 - index;
    }
  }
  return 0;
}

/** Picks the course whose price best matches the selected budget band (with interest as a tiebreaker). */
export function matchCourseByBudget(
  courses: MatchableCourse[],
  budgetAnswer: string,
  interestAnswer?: string
): MatchableCourse | null {
  if (!courses.length) return null;

  const { min, max } = parseBudgetRange(budgetAnswer);
  const inRange = courses.filter((course) => course.price >= min && course.price <= max);
  const pool = inRange.length > 0 ? inRange : courses;

  const scored = [...pool].sort((a, b) => {
    const aFit = Math.abs((a.price - (min + max) / 2));
    const bFit = Math.abs((b.price - (min + max) / 2));
    const aOver = a.price > max ? a.price - max : 0;
    const bOver = b.price > max ? b.price - max : 0;
    const aScore = aFit + aOver * 2 - interestScore(a, interestAnswer);
    const bScore = bFit + bOver * 2 - interestScore(b, interestAnswer);
    if (aScore !== bScore) return aScore - bScore;
    return a.price - b.price;
  });

  return scored[0] ?? null;
}
